import {API_COMMON_URL, SAVE_CLIENT_SECRET} from '../constants/constants';
import {
    cartCartSelector,
    cartIncludedProductsSelector,
    cartMainProduct, cartRenewSelector,
    cartCurrencySelector
} from '../selectors/cartSelector';
import {paymentPageTokenSelector, promoCodesListSelector, sessionKey} from '../selectors/pagesSelectors';
import {
    CHECKOUT,
    PAYMENT,
    PROCEED_TO_CHECKOUT_AFTER_AUTH,
    SUBSCRIPTION,
    TOKEN_ERROR_LIST
} from '../constants/checkoutConstants';
import isRenewAllowed from '../../resolvers/isRenewAllowed';
import { getLogger } from '../../lib/devMode';
import isPeriodDifferentByProducts from '../../resolvers/isPeriodDifferentByProducts';
import convertToBoolean from '../../lib/string/convertToBoolean';
import { toast } from 'react-toastify';
import {logout} from "./authActions";
import {setAccountFormIsVisible, setAccountFormRegisterState} from "./pagesActions";
import {getPromoFromArray} from "../../utils";
import { dispatchWithRetry } from '../../utils/dispatchWithRetry'

export const redirectToPayment = (provider = 'stripe', postDataPassed = {}) => (dispatch, getState) => {

    // Unset the redirection to payment gateway on auth if it's set.
    dispatch({ type: PROCEED_TO_CHECKOUT_AFTER_AUTH, data: null });

    const cart = cartCartSelector(getState());
    const user_token = sessionKey(getState());
    const payment_page_token = paymentPageTokenSelector(getState());
    const currency = cartCurrencySelector(getState())

    const promoCodesList = promoCodesListSelector(getState());
    const promo  = getPromoFromArray(promoCodesList);

    const mainProduct = cartMainProduct(getState());
    const autoUpgradeFlag = typeof cart?.autoUpgrade === 'string'
        ? convertToBoolean(cart?.autoUpgrade) && mainProduct?.product_id === 2
        : !!cart?.autoUpgrade && mainProduct?.product_id === 2;
    const featuredIncludedProducts = cartIncludedProductsSelector(getState())?.filter(
        (product) => {
            const inclusionFlag = product?.product_id !== mainProduct?.product_id && !!product?.tariff
                && !isPeriodDifferentByProducts(mainProduct, product);
            getLogger().debug({
                debug: 'Checkout > Featured product item',
                product,
                inclusionFlag
            })
            return inclusionFlag;
        }
    ).map(product => product.product_id);

    const storageParams = JSON.parse(localStorage.getItem('utm') || '{}');
    const utmParams = {};
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

    Object?.keys(storageParams)?.forEach(key => {
        if (utmKeys.includes(key)) {
            utmParams[key] = storageParams[key];
        }
    });

    let doBoardDisabled;

    if(+mainProduct?.product_id === 7){
        doBoardDisabled = !(mainProduct?.period?.billing_period === "Month" && +mainProduct?.period?.interval === 12 &&
            mainProduct?.period?.interval_bonus_unit === "month" && +mainProduct?.period?.interval_bonus === 3);
    } else doBoardDisabled = true;

    const getCurrency = () => mainProduct?.tariff?.cost_local && Object.keys(mainProduct?.tariff?.cost_local)?.includes(currency?.currency) ? {currency: currency.currency} : {}

    let postData = {
        user_token: user_token,
        payment_page_token: payment_page_token,
        ...utmParams,
        ...getCurrency(),
    };

    if (cart?.selectedCustomBills?.id) {
        postData.mode = PAYMENT;
        postData.custom_bills = cart?.selectedCustomBills?.id;
        postData.currency = 'USD';
        postData.cancel_url = window.location.origin + window.location.pathname + window.location.search;
        postData.source = 'P2';
    } else if (Object.keys(postDataPassed ?? {}).length > 0 ) {
        postData = {
            ...postData,
            ...postDataPassed
        }
    } else {
        postData = {
            ...postData,
            interval: mainProduct?.period?.billing_period,
            interval_count: mainProduct?.period?.interval,
            // Before submitting renew flag - ensure that it's possible to renew.
            mode: doBoardDisabled && isRenewAllowed(mainProduct?.period, mainProduct?.product_id) && cart?.renew ? SUBSCRIPTION : PAYMENT,
            tariff_id: mainProduct?.tariff?.tariff_id,
            extra_package: Number(mainProduct?.includeExtra ?? 0),
            auto_upgrade: autoUpgradeFlag ? 1 : 0,
            cancel_url: window.location.origin + window.location.pathname + window.location.search,
            source: 'P2', // Stands to the current universal payments page.
        };
    }

    if (promo && +promo?.product_id === +mainProduct?.product_id) {
        postData.promo_code = promo.promokey;
    }

    provider = typeof provider === 'string' ? provider.trim().toLowerCase() : null;
    if (!!provider && ['paypal', 'stripe'].includes(provider)) {
        postData.provider = provider;
    } else {
        throw new Error('Payment provider is not available');
    }

    postData.ui_mode = 'hosted';

    if (featuredIncludedProducts?.length) {
        postData.featured_product_ids = featuredIncludedProducts;
    }

    // SSL product.
    // If there is no domain selected - is considered purchase of a new one.
    // If at least one domain is selected - return "multiservice_ids" set.
    // For details @see: https://doboard.com/1/task/4966#comment_36078
    if (mainProduct?.product_id === 5 && cart?.domains?.length) {
        postData.multiservice_ids = cart?.domains;
    } else if (mainProduct?.product_id === 7 && cart?.account_id) {
        // For doBoard payment - account ID must be specified.
        postData.multiservice_ids = cart?.account_id;
    }

    dispatch({
        type: CHECKOUT,
        CallAPI: API_COMMON_URL + '?method_name=bill_checkout',
        postData: postData,
        showTokenError: true
    }).then(async (data) => {
        if (data?.apiData?.error_message) {
            if ( TOKEN_ERROR_LIST.includes(data?.apiData?.error_message)) {
                dispatch(logout()).then(() => {
                    dispatch(setAccountFormIsVisible(true));
                    dispatch(setAccountFormRegisterState(true));
                });
            } else {
                toast.error(`Order payment failed: ${data?.apiData?.error_message} (${data?.apiData?.error_no})`);
            }
        } else if (data?.apiData?.data?.operation_status) {
            if (provider === 'stripe') {
                window.location.href = data?.apiData?.data?.payment_url;
            } else if (provider === 'paypal') {
                window.location.href = data?.apiData?.data?.href;
            } else {
                toast.error('Not supported payment gateway');
            }
        }
    }).catch((error) => getLogger().error(error));
};

export const createEmbeddedPaymentSession = (totalNumber) => async (dispatch, getState) => {

    const sleep = new Promise((resolve) => setTimeout(resolve, 2000));

    await sleep;

    // Unset the redirection to payment gateway on auth if it's set.
    dispatch({ type: PROCEED_TO_CHECKOUT_AFTER_AUTH, data: null });

    const cart = cartCartSelector(getState());
    const user_token = sessionKey(getState());
    const payment_page_token = paymentPageTokenSelector(getState());
    const currency = cartCurrencySelector(getState())

    const promoCodesList = promoCodesListSelector(getState());
    const promo  = getPromoFromArray(promoCodesList);

    if (!payment_page_token && !user_token) {
        return false;
    }

    const mainProduct = cartMainProduct(getState());
    const autoUpgradeFlag = typeof cart?.autoUpgrade === 'string'
        ? convertToBoolean(cart?.autoUpgrade) && mainProduct?.product_id === 2
        : !!cart?.autoUpgrade && mainProduct?.product_id === 2;
    const featuredIncludedProducts = cartIncludedProductsSelector(getState())?.filter(
        (product) => {
            const inclusionFlag = product?.product_id !== mainProduct?.product_id && !!product?.tariff
                && !isPeriodDifferentByProducts(mainProduct, product);
            getLogger().debug({
                debug: 'Checkout > Featured product item',
                product,
                inclusionFlag
            })
            return inclusionFlag;
        }
    ).map(product => product.product_id);

    const storageParams = JSON.parse(localStorage.getItem('utm') || '{}');
    const utmParams = {};
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

    Object?.keys(storageParams)?.forEach(key => {
        if (utmKeys.includes(key)) {
            utmParams[key] = storageParams[key];
        }
    });

    let doBoardDisabled;

    if(+mainProduct?.product_id === 7){
        doBoardDisabled = !(mainProduct?.period?.billing_period === "Month" && +mainProduct?.period?.interval === 12 &&
            mainProduct?.period?.interval_bonus_unit === "month" && +mainProduct?.period?.interval_bonus === 3);
    } else doBoardDisabled = true;

    const getCurrency = () => mainProduct?.tariff?.cost_local && Object.keys(mainProduct?.tariff?.cost_local)?.includes(currency?.currency) ? {currency: currency?.currency} : {}

    let postData = {
        user_token: user_token,
        payment_page_token: payment_page_token,
        ...utmParams,
        ...getCurrency(),
    };

    if (cart?.selectedCustomBills?.id) {
        postData.mode = PAYMENT;
        postData.custom_bills = cart?.selectedCustomBills?.id;
        postData.currency = 'USD';
    } else  {
        postData = {
            ...postData,
            interval: mainProduct?.period?.billing_period,
            interval_count: mainProduct?.period?.interval,
            // Before submitting renew flag - ensure that it's possible to renew.
            mode: doBoardDisabled && isRenewAllowed(mainProduct?.period, mainProduct?.product_id) && cart?.renew ? SUBSCRIPTION : PAYMENT,
            tariff_id: mainProduct?.tariff?.tariff_id,
            extra_package: Number(mainProduct?.includeExtra ?? 0),
            auto_upgrade: autoUpgradeFlag ? 1 : 0,
            cancel_url: window.location.origin + window.location.pathname + window.location.search,
            source: 'P2', // Stands to the current universal payments page.
        }
    }

    if (featuredIncludedProducts?.length) {
        postData.featured_product_ids = featuredIncludedProducts;
    }

    // SSL product.
    // If there is no domain selected - is considered purchase of a new one.
    // If at least one domain is selected - return "multiservice_ids" set.
    // For details @see: https://doboard.com/1/task/4966#comment_36078
    if (mainProduct?.product_id === 5 && cart?.domains?.length) {
        postData.multiservice_ids = cart?.domains;
    } else if (mainProduct?.product_id === 7 && cart?.account_id) {
        // For doBoard payment - account ID must be specified.
        postData.multiservice_ids = cart?.account_id;
    }
    postData.ui_mode = 'embedded';
    postData.provider ='stripe';

    if (promo && +promo?.product_id === +mainProduct?.product_id) {
        postData.promo_code = promo.promokey;
    }

    const cartRenew  = cartRenewSelector(getState()) || false;
    const totalNumberRenew = totalNumber.toString()+cartRenew.toString()

    const res = await dispatchWithRetry(dispatch, {
        type: SAVE_CLIENT_SECRET,
        CallAPI: API_COMMON_URL + `?method_name=bill_checkout`,
        postData,
        showTokenError: false,
        showError: false,
        totalNumber: totalNumberRenew,
    });

    return {
        ...res,
        'provider': postData.provider,
    };

};
